import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FiArrowRight, FiStar, FiTruck, FiShield, FiRefreshCw, FiHeadphones } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { products, categories } from '../data/products';

const Home = () => {
  const featuredProducts = products.slice(0, 4);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  const stats = [
    { value: '50K+', label: 'Happy Customers' },
    { value: '2K+', label: 'Premium Products' },
    { value: '4.9★', label: 'Average Rating' },
    { value: '99%', label: 'Satisfaction Rate' },
  ];

  const features = [
    { icon: <FiTruck />, title: 'Free Shipping', desc: 'On all orders over ¥9,900' },
    { icon: <FiShield />, title: 'Secure Payment', desc: '100% secure transactions' },
    { icon: <FiRefreshCw />, title: 'Easy Returns', desc: '30-day hassle-free returns' },
    { icon: <FiHeadphones />, title: '24/7 Support', desc: 'Always here to help' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <main className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <img src="/hero-banner3.jpg" alt="Hero" className="hero-bg-img" />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content container">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="hero-text"
          >
            <motion.span
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              ✨ New Season Arrivals
            </motion.span>
            <h1 className="hero-title">
              Elevate Your
              <span className="hero-gradient"> Lifestyle</span>
            </h1>
            <p className="hero-subtitle">
              Discover premium products curated for those who demand the extraordinary.
              From tech to fashion — excellence in every detail.
            </p>
            <div className="hero-actions">
              <Link to="/shop" className="btn btn-primary btn-lg">
                Shop Now
                <FiArrowRight />
              </Link>
              <Link to="/about" className="btn btn-outline btn-lg">
                Our Story
              </Link>
            </div>
            <div className="hero-social-proof">
              <div className="trust-avatars">
                {['👤', '👤', '👤', '👤'].map((_, i) => (
                  <span key={i} className="trust-avatar" style={{ '--i': i }}>
                    {String.fromCodePoint(0x1F464 + i)}
                  </span>
                ))}
              </div>
              <div>
                <div className="trust-stars">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} size={14} fill="#F59E0B" stroke="#F59E0B" />
                  ))}
                </div>
                <span className="trust-text">50,000+ Happy Customers</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="scroll-dot" />
        </motion.div>
      </section>

      {/* Stats */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <motion.div
            className="stats-grid"
            variants={containerVariants}
            initial="hidden"
            animate={statsInView ? 'visible' : 'hidden'}
          >
            {stats.map((stat, i) => (
              <motion.div key={i} className="stat-item" variants={itemVariants}>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="categories-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Shop by Category</h2>
            <p className="section-subtitle">Find exactly what you're looking for</p>
          </motion.div>
          <div className="categories-grid">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={`/shop?category=${cat.name}`} className="category-card">
                  <div className="category-icon" style={{ background: `${cat.color}22`, color: cat.color }}>
                    {cat.icon}
                  </div>
                  <div className="category-info">
                    <span className="category-name">{cat.name}</span>
                    <span className="category-count">{cat.count} products</span>
                  </div>
                  <FiArrowRight className="category-arrow" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Featured Products</h2>
            <p className="section-subtitle">Handpicked just for you</p>
            <Link to="/shop" className="section-link">
              View All <FiArrowRight />
            </Link>
          </motion.div>
          <div className="products-grid">
            {featuredProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="promo-banner section">
        <div className="container">
          <motion.div
            className="promo-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="promo-content">
              <span className="promo-tag">Limited Time Offer</span>
              <h2 className="promo-title">Summer Sale is Live</h2>
              <p className="promo-desc">Up to 40% off on selected premium items. Don't miss out!</p>
              <Link to="/shop" className="btn btn-primary btn-lg">
                Shop the Sale <FiArrowRight />
              </Link>
            </div>
            <div className="promo-decoration">
              <div className="promo-circle promo-circle-1" />
              <div className="promo-circle promo-circle-2" />
              <span className="promo-big-text">40%</span>
              <span className="promo-off">OFF</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section section">
        <div className="container">
          <div className="features-grid">
            {features.map((f, i) => (
              <motion.div
                key={i}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div className="feature-icon">{f.icon}</div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
